import { Clear } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { typingTimeOut } from '../../../constants';
import { companyServices } from '../../../services/companyServices';
import { Company } from '../../../models/company';
import { FreelancerType } from '../../../enums/freelancerType';

export interface FilterCompaniesForm {
  companiesIds?: Company[];
  markup?: string;
  freelancer?: string;
  status?: string[];
}

interface Props {
  handleFormSubmit: (data: FilterCompaniesForm) => void;
  filterData: FilterCompaniesForm;
}

const defaultValues: FilterCompaniesForm = {
  companiesIds: [],
  markup: '',
  freelancer: '',
  status: []
};

const FilterCompanies = (props: Props): JSX.Element => {
  const [filterData, setFilterData] = useState<FilterCompaniesForm>(
    props.filterData
  );
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    getCompanies();
  }, []);

  const getCompanies = () => {
    companyServices.getCompaniesAutocomplete({}).then((response) => {
      setCompanies(response.data);
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string[]> | SelectChangeEvent
  ) => {
    const {
      target: { value, name }
    } = event;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              multiple
              id="company"
              options={companies}
              getOptionLabel={(option) => `${option.name} `}
              value={filterData.companiesIds ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  companiesIds: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => (
                <TextField {...params} label="Company" />
              )}
            />
            <TextField
              sx={{ maxWidth: 245 }}
              label="Markup"
              name="markup"
              value={filterData.markup}
              onChange={handleChange}
            />
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="status">Status</InputLabel>
              <Select
                label="status"
                name="status"
                value={filterData.status}
                onChange={handleSelectChange}
                input={<OutlinedInput label="status" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value={'ACTIVE'}>ACTIVE</MenuItem>
                <MenuItem value={'INACTIVE'}>INACTIVE</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="freelancer">Freelancers</InputLabel>
              <Select
                labelId="freelancer"
                label="freelancer"
                name="freelancer"
                value={filterData.freelancer}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Freelancers" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.keys(FreelancerType).map((freelancerType) => (
                  <MenuItem key={freelancerType} value={freelancerType}>
                    {
                      FreelancerType[
                        freelancerType as keyof typeof FreelancerType
                      ]
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterCompanies;
